import React, { Suspense, useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useGLTF, Environment, Float, Preload } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import * as THREE from 'three';
import { useLanguage } from '@/LanguageContext';

// ════════════════════════════════════════════════════════════════════════════
// 🔧 HIZLI AYARLAR — En sık değiştirilen değerler burada toplandı
// ════════════════════════════════════════════════════════════════════════════

const API = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const useSettings = () => {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    fetch(`${API}/settings`).then(r => r.json()).then(setSettings).catch(() => { });
  }, []);
  return settings;
};

const SOCIAL_PLATFORMS = [
  { key: 'social_instagram', label: 'Instagram', color: '#bc13fe', d: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' },
  { key: 'social_twitter', label: 'X / Twitter', color: '#00f0ff', d: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
  { key: 'social_linkedin', label: 'LinkedIn', color: '#00f0ff', d: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
  { key: 'social_behance', label: 'Behance', color: '#00ff88', d: 'M6.938 4.503c.702 0 1.34.06 1.92.188.577.13 1.07.33 1.485.61.41.28.733.65.96 1.12.225.47.34 1.05.34 1.73 0 .74-.17 1.36-.507 1.86-.338.5-.837.9-1.502 1.22.906.26 1.576.72 2.022 1.37.448.66.665 1.45.665 2.36 0 .75-.13 1.39-.41 1.93-.28.55-.67 1-1.16 1.35-.49.348-1.05.6-1.68.767-.63.166-1.3.25-1.99.25H0V4.503h6.938zm-.34 4.987c.55 0 1-.13 1.35-.39.35-.26.52-.67.52-1.22 0-.3-.06-.55-.16-.76-.1-.2-.25-.37-.43-.5-.18-.12-.4-.21-.64-.26-.24-.05-.5-.07-.78-.07H3.64v3.2h2.958zm.157 5.22c.29 0 .57-.03.83-.08.27-.06.5-.15.7-.3.2-.14.36-.34.48-.58.12-.24.17-.55.17-.92 0-.74-.2-1.27-.61-1.58-.41-.31-.96-.47-1.66-.47H3.64v3.93h3.115zm8.102-2.36c.27.26.67.4 1.2.4.37 0 .7-.1.97-.28.27-.19.44-.39.5-.6h2.16c-.35 1.07-.87 1.84-1.58 2.28-.71.44-1.57.67-2.57.67-.7 0-1.32-.11-1.88-.34-.56-.23-1.03-.55-1.43-.97-.39-.42-.69-.92-.9-1.5-.21-.58-.31-1.21-.31-1.9 0-.67.11-1.29.32-1.86.21-.57.51-1.06.9-1.47.39-.41.86-.73 1.4-.95.55-.22 1.14-.34 1.8-.34.74 0 1.38.14 1.93.43.55.28 1 .67 1.36 1.16.36.49.62 1.05.78 1.68.16.63.21 1.29.17 1.98h-6.43c0 .57.18 1.09.45 1.35zm2.29-3.88c-.22-.24-.56-.36-1.02-.36-.3 0-.55.05-.75.16-.2.1-.36.23-.48.38-.12.15-.2.3-.25.47-.05.17-.08.32-.09.46h3.03c-.07-.48-.22-.87-.44-1.11zm-5.328-7.55h4.7v1.2h-4.7V1.9z' },
  { key: 'social_youtube', label: 'YouTube', color: '#ff003c', d: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
  { key: 'social_telegram', label: 'Telegram', color: '#00f0ff', d: 'M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z' },
  { key: 'social_tiktok', label: 'TikTok', color: '#bc13fe', d: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z' },
  { key: 'social_github', label: 'GitHub', color: '#ffffff', d: 'M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12' },
  { key: 'social_dribbble', label: 'Dribbble', color: '#ff003c', d: 'M12 24C5.385 24 0 18.615 0 12S5.385 0 12 0s12 5.385 12 12-5.385 12-12 12zm10.12-10.358c-.35-.11-3.17-.953-6.384-.438 1.34 3.684 1.887 6.684 1.992 7.308 2.3-1.555 3.936-4.02 4.395-6.87zm-6.115 7.808c-.153-.9-.75-4.032-2.19-7.77l-.066.02c-5.79 2.015-7.86 6.017-8.04 6.39 1.73 1.35 3.92 2.166 6.29 2.166 1.42 0 2.77-.29 4-.814zm-11.62-2.073c.232-.4 3.045-5.055 8.332-6.765.135-.045.27-.084.405-.12-.26-.585-.54-1.167-.832-1.74C7.17 11.775 2.206 11.71 1.756 11.7l-.004.312c0 2.633.998 5.037 2.634 6.855zm-2.42-8.955c.46.008 4.683.026 9.477-1.248-1.698-3.018-3.53-5.558-3.8-5.928-2.868 1.35-5.01 3.99-5.676 7.18zM9.6 2.052c.282.38 2.145 2.914 3.822 6 3.645-1.365 5.19-3.44 5.373-3.702-1.81-1.61-4.19-2.586-6.795-2.586-.825 0-1.63.1-2.4.285zm10.335 3.483c-.218.29-1.935 2.493-5.724 4.04.24.49.47.985.68 1.486.08.18.15.36.22.53 3.41-.43 6.8.26 7.14.33-.02-2.42-.88-4.64-2.31-6.38z' },
  { key: 'social_imdb', label: 'IMDb', color: '#F5C518', isImdb: true },
];

const SocialIcon = ({ href, color, d, label, isImdb }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <a href={href} target="_blank" rel="noreferrer" aria-label={label}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 36, height: 36,
        border: `1px solid ${hovered ? color : 'rgba(255,255,255,0.1)'}`,
        transition: 'all 0.3s ease',
        boxShadow: hovered ? `0 0 12px ${color}, 0 0 24px ${color}40` : 'none',
        background: hovered ? `${color}15` : 'transparent',
      }}>
      {isImdb ? (
        <svg viewBox="0 0 72 28" fill="none" width="18" height="7" xmlns="http://www.w3.org/2000/svg"
          style={{ transition: 'filter 0.3s ease', filter: hovered ? `drop-shadow(0 0 4px ${color}) grayscale(0)` : 'grayscale(1)', opacity: hovered ? 1 : 0.6 }}>
          <rect width="72" height="28" rx="4" fill="#F5C518" />
          <text x="6" y="21" fontFamily="Arial Black, Arial" fontWeight="900" fontSize="17" fill="#000000" letterSpacing="-0.5">IMDb</text>
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" width="14" height="14"
          fill={hovered ? color : 'rgba(255,255,255,0.4)'}
          style={{ transition: 'fill 0.3s ease', filter: hovered ? `drop-shadow(0 0 4px ${color})` : 'none' }}>
          <path d={d} />
        </svg>
      )}
    </a>
  );
};

const CONFIG = {
  // 📁 3D model dosyasının adı (public/ klasöründe olmalı)
  MODEL_PATH: '/knoxopia_logo_3d.glb',

  // 📐 Logonun boyutu — büyütmek için artır, küçültmek için azalt
  LOGO_SCALE: 0.65,

  // 🎥 Kameranın logodan uzaklığı — artırırsan logo küçük görünür
  CAMERA_Z: 7,
  // 🎥 Kamera görüş açısı — artırırsan daha geniş görünür
  CAMERA_FOV: 42,

  // 🖱️ Fare hareketi logo yatay dönüş şiddeti (0 = yok, 1 = çok)
  MOUSE_X_STRENGTH: 0.7,
  // 🖱️ Fare hareketi logo dikey dönüş şiddeti
  MOUSE_Y_STRENGTH: 0.5,
  // 🖱️ Farenin takip hızı — düşürürsen daha tembel/soft takip eder (0.01–0.1)
  MOUSE_LERP: 0.04,

  // 🔄 Sürekli kendi etrafında dönme hızı — 0 yapınca durur
  AUTO_ROTATE_SPEED: 0.06,

  // ✨ Chrome materyal parlaklığı — 0 (mat) ile 1 (ayna) arası
  METALNESS: 1.0,
  ROUGHNESS: 0.03,
  // ✨ Yansıma yoğunluğu — artırırsan daha parlak yansıma
  ENV_MAP_INTENSITY: 3.5,

  // 💡 Ana beyaz ışık şiddeti
  LIGHT_MAIN_INTENSITY: 8,
  // 💡 Dolgu ışığı şiddeti
  LIGHT_FILL_INTENSITY: 4,
  // 💡 Alt sıcak rim ışığı rengi
  LIGHT_RIM_COLOR: '#ff6a00',
  // 💡 Arka mavi aksan ışığı rengi
  LIGHT_ACCENT_COLOR: '#0044ff',

  // ✦ Partikül sayısı — artırırsan daha fazla parıltı
  PARTICLE_COUNT: 220,
  // ✦ Partikül rengi
  PARTICLE_COLOR: '#aaccff',
  // ✦ Partikül boyutu
  PARTICLE_SIZE: 0.028,
  // ✦ Partikül saydamlığı (0–1)
  PARTICLE_OPACITY: 0.35,

  // ⏱️ Giriş animasyonu süresi (saniye) — canvas blur'dan açılırken
  INTRO_BLUR_DURATION: 2.8,
  // ⏱️ Logo kaybolma hızı — düşürürsen daha yavaş küçülür (0.3–1.0)
  EXIT_SPEED: 0.62,

  // 📝 Üst yazı — "Welcome to" metni
  WELCOME_TEXT: 'Welcome to',
  // 📝 Alt buton yazısı
  CTA_TEXT: 'KNOXOPIA',
  // 📝 Yazı fontu
  FONT_FAMILY: 'JetBrains Mono',
};
// ════════════════════════════════════════════════════════════════════════════


// ─── Chrome Materyal Uygulayıcı ──────────────────────────────────────────────
// Logonun tüm yüzeylerine chrome/ayna materyal uygular
// Rengi veya parlaklığı değiştirmek için yukarıdaki CONFIG'i kullan
function applyChromeMaterial(obj) {
  obj.traverse((child) => {
    if (child.isMesh) {
      child.material = new THREE.MeshStandardMaterial({
        color: new THREE.Color('#ffffff'),
        metalness: CONFIG.METALNESS,
        roughness: CONFIG.ROUGHNESS,
        envMapIntensity: CONFIG.ENV_MAP_INTENSITY,
      });
      child.castShadow = true;
    }
  });
}

// ─── 3D Logo Bileşeni ─────────────────────────────────────────────────────────
// Logoyu yükler, fare takibini ve çıkış animasyonunu yönetir
function KnoxopiaLogoModel({ modelPath, mousePos, isExiting, onGone, logoScale, autoRotate, metalness, roughness, exitSpeed = CONFIG.EXIT_SPEED, spinExit = false }) {
  const { scene } = useGLTF(modelPath);
  const groupRef = useRef();
  const scaleRef = useRef(0);       // Giriş animasyonu için mevcut scale değeri
  const exitProgress = useRef(0);   // Çıkış animasyonu için 0→1 ilerleme
  const goneFired = useRef(false);  // onGone'un bir kez tetiklenmesi için kilit

  // Modeli klonla — orijinal scene'i bozmaz, birden fazla yerde kullanılabilir
  const clonedScene = useMemo(() => {
    const clone = scene.clone(true);
    applyChromeMaterial(clone, metalness, roughness);
    return clone;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, metalness, roughness]);

  // Bileşen kaldırılınca Three.js objelerini temizle (bellek sızıntısı önlenir)
  useEffect(() => {
    return () => {
      clonedScene.traverse((child) => {
        if (child.isMesh) {
          child.geometry?.dispose();
          if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
          else child.material?.dispose();
        }
      });
    };
  }, [clonedScene]);

  // Her kare çalışan animasyon döngüsü
  useFrame((state, delta) => {
    if (!groupRef.current) return;

    if (!isExiting) {
      // ── Normal durum: Büyüyerek giriş + fare takibi ──

      // Scale yavaşça CONFIG.LOGO_SCALE değerine lerp eder (yumuşak büyüme)
      scaleRef.current = THREE.MathUtils.lerp(scaleRef.current, logoScale ?? CONFIG.LOGO_SCALE, delta * 1.0);
      groupRef.current.scale.setScalar(scaleRef.current);

      // Farenin pozisyonuna göre hedef rotasyon hesapla
      // Eksi işareti: farenin gittiği yöne döner (artı yapınca ters döner)
      const targetX = -mousePos.current.y * CONFIG.MOUSE_Y_STRENGTH;
      const targetY = mousePos.current.x * CONFIG.MOUSE_X_STRENGTH;

      // Smooth lerp ile takip — ani değil, yavaş kayar
      groupRef.current.rotation.x = THREE.MathUtils.lerp(groupRef.current.rotation.x, targetX, CONFIG.MOUSE_LERP);
      groupRef.current.rotation.y = THREE.MathUtils.lerp(groupRef.current.rotation.y, targetY, CONFIG.MOUSE_LERP);

      // Sürekli hafif kendi etrafında dönme
      groupRef.current.rotation.y += delta * (autoRotate ?? CONFIG.AUTO_ROTATE_SPEED);

    } else {
      // ── Çıkış animasyonu ──
      // İki mod:
      //  spinExit=true  (kamera dalışı KAPALI): hızlanan spiral dönüşle dinamik kayboluş
      //  spinExit=false (solucan deliği AÇIK): kamerayla senkron, yavaş içeri çekilme

      const speedMul = spinExit ? 1.8 : 1.0;   // tünelsiz mod daha çabuk biter
      exitProgress.current = Math.min(exitProgress.current + delta * exitSpeed * speedMul, 1);
      const t = exitProgress.current;

      // spinExit: t³ — hareket daha erken başlar (durgunluk hissi yok)
      // tünel modu: t⁴ — kamera dalışıyla birlikte sona doğru ivmelenir
      const ease = spinExit ? t * t * t : t * t * t * t;

      // Scale: LOGO_SCALE'den 0'a küçülür (tam yok olur)
      groupRef.current.scale.setScalar(THREE.MathUtils.lerp(logoScale ?? CONFIG.LOGO_SCALE, 0, ease));

      // Geriye çekilme (derinlik hissi) — spiral modda daha derine
      groupRef.current.position.z = THREE.MathUtils.lerp(0, spinExit ? -3.2 : -2.5, ease);

      if (spinExit) {
        // Hızlanan spiral: küçüldükçe dönüş coşar — girdaba kapılmış gibi
        groupRef.current.rotation.y += delta * (0.4 + ease * 11.0);
      } else {
        // Tünel modunda hafif dönüş yeter (burguyu kamera atıyor)
        groupRef.current.rotation.y += delta * 0.08;
      }

      // %98'e gelince tamamen kayboldu say → siteyi aç
      if (t >= 0.98 && !goneFired.current) {
        goneFired.current = true;
        onGone?.();
      }
    }
  });

  return (
    // Float: logoya hafif süzülme animasyonu verir
    // speed: süzülme hızı | rotationIntensity: dönel sallanma | floatIntensity: yukarı-aşağı
    <Float speed={1.2} rotationIntensity={isExiting ? 0 : 0.1} floatIntensity={isExiting ? 0 : 0.3}>
      <group ref={groupRef} scale={0} position={[0, 0, 0]}>
        <primitive object={clonedScene} dispose={null} />
      </group>
    </Float>
  );
}

// ─── Three.js Renderer Ayarları ───────────────────────────────────────────────
// Gölge ve renk tonu ayarları — genelde dokunmana gerek yok
function SceneSetup() {
  const { gl } = useThree();
  useEffect(() => {
    gl.shadowMap.enabled = true;
    gl.shadowMap.type = THREE.PCFSoftShadowMap;
    // ACESFilmicToneMapping: sinematik renk tonu — ReinhardToneMapping ile değiştirilebilir
    gl.toneMapping = THREE.ACESFilmicToneMapping;
    // Parlaklık çarpanı — artırırsan sahne daha aydınlık
    gl.toneMappingExposure = 1.0;
  }, [gl]);
  return null;
}

// ─── Chrome Işıklandırması ────────────────────────────────────────────────────
// Işıkları değiştirmek için CONFIG içindeki LIGHT_ değerlerini düzenle
// İki ana ışık fare ile birlikte hareket eder → chrome'da canlı yansıma
function ChromeLights({ mousePos }) {
  const light1Ref = useRef(); // Ana beyaz ışık — farenin peşinden gider
  const light2Ref = useRef(); // Dolgu ışığı — ters yönde gider

  useFrame(() => {
    if (!light1Ref.current || !light2Ref.current) return;

    // Ana ışık fareyle hareket eder (5 = hareket şiddeti)
    light1Ref.current.position.x = THREE.MathUtils.lerp(light1Ref.current.position.x, mousePos.current.x * 5, 0.05);
    light1Ref.current.position.y = THREE.MathUtils.lerp(light1Ref.current.position.y, mousePos.current.y * 3 + 2, 0.05);
    // Dolgu ışığı ters yönde gider
    light2Ref.current.position.x = THREE.MathUtils.lerp(light2Ref.current.position.x, -mousePos.current.x * 4, 0.03);
  });

  return (
    <>
      {/* Ana beyaz ışık — chrome'un parlak yansımasını sağlar */}
      <pointLight ref={light1Ref} position={[4, 2, 4]} intensity={CONFIG.LIGHT_MAIN_INTENSITY} color="#ffffff" distance={25} decay={1.5} />
      {/* Sol dolgu ışığı — soğuk mavi ton */}
      <pointLight ref={light2Ref} position={[-4, -1, 3]} intensity={CONFIG.LIGHT_FILL_INTENSITY} color="#c8e8ff" distance={20} decay={2} />
      {/* Alt rim ışığı — sıcak turuncu/kırmızı ton, rengi CONFIG'den değiştir */}
      <pointLight position={[0, -4, 2]} intensity={3} color={CONFIG.LIGHT_RIM_COLOR} distance={15} decay={2} />
      {/* Arka aksan ışığı — mavi/mor ton, rengi CONFIG'den değiştir */}
      <pointLight position={[0, 3, -6]} intensity={5} color={CONFIG.LIGHT_ACCENT_COLOR} distance={20} decay={1.8} />
      {/* Çok düşük ambient — tamamen karanlık kalmasın diye */}
      <ambientLight intensity={0.04} />
      {/* HDR environment — chrome yansıması için şart, preset değiştirilebilir: */}
      {/* 'studio' | 'city' | 'dawn' | 'night' | 'sunset' | 'warehouse' | 'forest' | 'lobby' */}
      <Environment preset="studio" blur={0} background={false} />
    </>
  );
}

// ─── Partikül Sistemi ─────────────────────────────────────────────────────────
// Etraftaki hafif parıltı noktaları
// Sayı, renk, boyut için CONFIG'deki PARTICLE_ değerlerini kullan
function Particles({ isExiting, count, color, size }) {
  const meshRef = useRef();
  const COUNT = count ?? CONFIG.PARTICLE_COUNT;

  const { positions, speeds, phases } = useMemo(() => {
    const positions = new Float32Array(COUNT * 3);
    const speeds = new Float32Array(COUNT);
    const phases = new Float32Array(COUNT);
    for (let i = 0; i < COUNT; i++) {
      // Dağılım alanı: 16x10x8 birim — logonun etrafına yayılır
      positions[i * 3 + 0] = (Math.random() - 0.5) * 16;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 10;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 8 - 2;
      speeds[i] = 0.15 + Math.random() * 0.3;
      phases[i] = Math.random() * Math.PI * 2;
    }
    return { positions, speeds, phases };
    // eslint-disable-next-line
  }, []);

  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(positions.slice(), 3));
    return g;
  }, [positions]);

  useFrame((state, delta) => {
    if (!meshRef.current) return;
    const pos = meshRef.current.geometry.attributes.position;
    const t = state.clock.elapsedTime;
    for (let i = 0; i < COUNT; i++) {
      // Yavaşça yukarı süzülür, ekranın üstüne çıkınca alttan döner
      pos.setY(i, positions[i * 3 + 1] + (t * speeds[i] * 0.04) % 10 - 5);
      // Hafif sinüs dalgasıyla sağa-sola sallanır
      pos.setX(i, positions[i * 3 + 0] + Math.sin(t * 0.2 + phases[i]) * 0.3);
    }
    pos.needsUpdate = true;
    // Çıkışta partiküller yavaşça söner
    if (isExiting) {
      meshRef.current.material.opacity = Math.max(0, meshRef.current.material.opacity - delta * 0.8);
    }
  });

  return (
    <points ref={meshRef} geometry={geo}>
      <pointsMaterial
        size={size ?? CONFIG.PARTICLE_SIZE}
        color={color ?? CONFIG.PARTICLE_COLOR}
        transparent
        opacity={CONFIG.PARTICLE_OPACITY}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

// ─── Galaktik Nebula Arka Planı ───────────────────────────────────────────────
// Shader tabanlı dumanlı/akışkan nebula: domain-warped FBM gürültüsü, 3 renkli
// katman, ince yıldız parıltıları. Fareyle hafifçe sürüklenir, çıkışta söner.

const GALAXY_VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const GALAXY_FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uTime;
  uniform float uOpacity;
  uniform float uIntensity;
  uniform float uDisperse;
  uniform float uWarp;
  uniform float uWarpSpeed;
  uniform float uWarpBright;
  uniform float uTwist;
  uniform float uAspect;
  uniform vec2  uMouse;
  uniform vec3  uColorA;
  uniform vec3  uColorB;
  uniform vec3  uColorC;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }

  float noise(vec2 p) {
    vec2 i = floor(p); vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
               mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
  }

  // 4 oktav — 5'ten indirildi: görsel fark yok denecek kadar az, %20 daha ucuz
  float fbm(vec2 p) {
    float v = 0.0; float a = 0.5;
    mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
    for (int i = 0; i < 4; i++) { v += a * noise(p); p = rot * p * 2.0 + 10.0; a *= 0.5; }
    return v;
  }

  void main() {
    vec2 uv = vUv - 0.5;
    uv.x *= uAspect;                   // gerçek ekran oranına göre esnet
    float t = uTime * 0.035;

    // Vinyet — dağıtma etkisi bozmasın diye ORİJİNAL koordinattan hesaplanır
    float vig = smoothstep(0.95, 0.2, length(uv));
    vec2 uvo = uv;   // solucan deliği tüneli için orijinal merkezli koordinat

    // Fare dağıtma: imleç çevresindeki duman dışa itilir (uDisperse=0 → kapalı)
    vec2 mp = vec2(uMouse.x * 0.9, uMouse.y * 0.5);   // fareyi uv uzayına taşı
    vec2 dvec = uv - mp;
    float dist = length(dvec);
    vec2 dir = dvec / max(dist, 1e-3);                 // merkez tekilliği yok
    float force = uDisperse * exp(-dist * dist * 4.0) * smoothstep(0.0, 0.15, dist);
    uv -= dir * force;                                 // içe örnekle → duman DIŞA dağılmış görünür
    vec2 m = uMouse * 0.06;                            // çok hafif genel sürüklenme

    // Çift katmanlı domain warp → dumanın girdap gibi akması
    vec2 q = vec2(fbm(uv * 2.0 + t),        fbm(uv * 2.0 - t * 0.7 + 4.0));
    vec2 r = vec2(fbm(uv * 2.0 + q * 1.6 + vec2(1.7, 9.2) + m),
                  fbm(uv * 2.0 + q * 1.6 + vec2(8.3, 2.8) - m));
    float f = fbm(uv * 2.0 + r * 1.9);

    // 3 renkli nebula karışımı
    vec3 col = mix(uColorA, uColorB, clamp(f * f * 2.4, 0.0, 1.0));
    col = mix(col, uColorC, clamp(length(q) * 0.75, 0.0, 1.0));
    col *= f * uIntensity;

    // ── Solucan deliği tüneli (çıkışta uWarp 0→1 ile devreye girer) ──
    // 1/r projeksiyonu: halkalar merkeze doğru akar → tünelde uçuş hissi
    // uWarpSpeed / uTwist / uWarpBright admin panelden ayarlanır
    if (uWarp > 0.001) {
      float wr = max(length(uvo), 0.03);
      float wa = atan(uvo.y, uvo.x) + uTime * 0.5 * uTwist * uWarp;   // burgu dönüşü
      vec2 wuv = vec2(wa * 0.955, 0.35 / wr + uTime * (0.4 + 1.1 * uWarp) * uWarpSpeed);
      float wn = fbm(wuv * vec2(3.0, 1.5));
      vec3 wcol = mix(uColorA, uColorB, wn);
      wcol = mix(wcol, uColorC, wn * wn);
      wcol *= (0.35 + wn * 1.6) * smoothstep(1.5, 0.12, wr) * uIntensity * uWarpBright;
      wcol += uColorC * exp(-wr * 4.0) * (0.4 + uWarp * 0.9) * uWarpBright;  // parlayan çekirdek
      col = mix(col, wcol, uWarp);
      // Kenar yumuşatma: tünelde vinyet dairesel maskeye geçer, sert köşe kalmaz
      vig = mix(vig, smoothstep(1.35, 0.2, wr), uWarp);
    }

    gl_FragColor = vec4(col * vig * uOpacity, 1.0);
  }
`;

function GalaxyBackdrop({
  mousePos, isExiting, color1, color2, color3, intensity, speed,
  disperse = true, warpExit = true, warpSpeed = 1, warpBright = 1, twist = 0.5, exitSpeed = 0.62,
}) {
  const meshRef = useRef();
  const uniforms = useMemo(() => ({
    uTime:       { value: 0 },
    uOpacity:    { value: 0 },
    uIntensity:  { value: intensity ?? 0.9 },
    uDisperse:   { value: disperse ? 0.32 : 0 },
    uWarp:       { value: 0 },
    uWarpSpeed:  { value: warpSpeed ?? 1 },
    uWarpBright: { value: warpBright ?? 1 },
    uTwist:      { value: twist ?? 0.5 },
    uAspect:     { value: 1.8 },
    uMouse:      { value: new THREE.Vector2(0, 0) },
    uColorA:     { value: new THREE.Color(color1 ?? '#1b2a6e') },
    uColorB:     { value: new THREE.Color(color2 ?? '#bc13fe') },
    uColorC:     { value: new THREE.Color(color3 ?? '#00f0ff') },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // Admin'den gelen ayarlar değişince uniform'ları güncelle
  useEffect(() => {
    uniforms.uColorA.value.set(color1 ?? '#1b2a6e');
    uniforms.uColorB.value.set(color2 ?? '#bc13fe');
    uniforms.uColorC.value.set(color3 ?? '#00f0ff');
    uniforms.uIntensity.value = intensity ?? 0.9;
    uniforms.uDisperse.value = disperse ? 0.32 : 0;
    uniforms.uWarpSpeed.value = warpSpeed ?? 1;
    uniforms.uWarpBright.value = warpBright ?? 1;
    uniforms.uTwist.value = twist ?? 0.5;
  }, [color1, color2, color3, intensity, disperse, warpSpeed, warpBright, twist, uniforms]);

  useFrame((state, delta) => {
    // Tünelde zaman hafifçe ivmelenir (aşırı hız düzeltildi: 2.5 → 0.6)
    uniforms.uTime.value += delta * (speed ?? 1) * (1 + uniforms.uWarp.value * 0.6);
    uniforms.uMouse.value.lerp(
      new THREE.Vector2(mousePos.current.x, mousePos.current.y), 0.06
    );
    // Çıkışta solucan deliğine morph — kamera/logo ile AYNI hız tabanında (senkron)
    const warpTarget = isExiting && warpExit ? 1 : 0;
    uniforms.uWarp.value = THREE.MathUtils.lerp(uniforms.uWarp.value, warpTarget, delta * exitSpeed * 2.6);
    // Opaklık: tünelli çıkışta görünür kalır; tünel kapalıysa sönerek çıkar
    const target = isExiting ? (warpExit ? 1 : 0) : 1;
    uniforms.uOpacity.value = THREE.MathUtils.lerp(
      uniforms.uOpacity.value, target, delta * (isExiting && !warpExit ? 1.8 : 0.5)
    );

    // ── Düzlemi kameraya kilitle ve görüşü TAM kaplayacak şekilde ölçekle ──
    // Kamera dalarken/dönerken kenar veya köşe asla görünmez (dikdörtgen sorunu çözümü)
    const m = meshRef.current;
    if (m) {
      const cam = state.camera;
      const dist = 12;
      m.position.copy(cam.position);
      m.quaternion.copy(cam.quaternion);
      m.translateZ(-dist);
      const h = 2 * dist * Math.tan(THREE.MathUtils.degToRad(cam.fov) / 2);
      const aspect = state.size.width / state.size.height;
      m.scale.set(h * aspect * 1.15, h * 1.15, 1);   // %15 güvenlik payı
      uniforms.uAspect.value = aspect;
    }
  });

  return (
    <mesh ref={meshRef} renderOrder={-1} frustumCulled={false}>
      <planeGeometry args={[1, 1]} />
      <shaderMaterial
        uniforms={uniforms}
        vertexShader={GALAXY_VERT}
        fragmentShader={GALAXY_FRAG}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

// ─── Çıkış Kamera Dalışı ──────────────────────────────────────────────────────
// Tıklanınca kamera, içeri kaçan logoyla birlikte sahnenin derinliğine dalar;
// FOV genişler (hız hissi) — nebula büyüyerek "siteye ışınlanma" etkisi verir.
function ExitCameraRig({ isExiting, baseZ, baseFov, enabled = true, exitSpeed = 0.62, targetFov = 80, twist = 0.5 }) {
  const prog = useRef(0);
  useFrame((state, delta) => {
    if (!isExiting || !enabled) return;
    prog.current = Math.min(prog.current + delta * exitSpeed, 1);
    const t = prog.current;
    const ease = t * t * t; // başta yavaş, sona doğru hızlanan dalış
    state.camera.position.z = THREE.MathUtils.lerp(baseZ, -2.2, ease);
    state.camera.fov = THREE.MathUtils.lerp(baseFov, targetFov, ease); // genişleme → hız hissi
    state.camera.rotation.z = ease * 0.6 * twist;                      // burgu — dumanla senkron
    state.camera.updateProjectionMatrix();
  });
  return null;
}

// ─── Ana SplashScreen Bileşeni ────────────────────────────────────────────────
export const SplashScreen = ({ onEnter }) => {
  const { t } = useLanguage();
  const apiSettings = useSettings();
  const mousePos = useRef({ x: 0, y: 0 });
  const containerRef = useRef();

  // Animasyon aşamaları:
  // 'intro'    → Sayfa yeni açıldı, canvas henüz yok
  // 'idle'     → Logo görünür, yazılar bekleme modunda
  // 'exiting'  → Tıklandı, logo küçülüyor
  // 'blurring' → Logo kayboldu, siyah fade başladı
  const [phase, setPhase] = useState('intro');
  const [showCanvas, setShowCanvas] = useState(false);

  // 300ms sonra canvas'ı göster (ilk siyah ekranın blur'dan açılması için)
  // 300 değerini değiştirince ilk siyah bekleme süresi değişir (ms)
  useEffect(() => {
    const t = setTimeout(() => setShowCanvas(true), 300);
    return () => clearTimeout(t);
  }, []);

  // Canvas açılınca 200ms sonra yazıları göster
  // 200 değerini değiştirince yazıların ne kadar geç çıkacağı değişir (ms)
  useEffect(() => {
    if (!showCanvas) return;
    const t = setTimeout(() => setPhase('idle'), 200);
    return () => clearTimeout(t);
  }, [showCanvas]);

  // Logo tamamen kaybolunca çağrılır
  // 500ms sonra onEnter tetiklenir → App.js'deki hasEntered true olur → site açılır
  // 500 değerini değiştirince logo kaybolduktan siteye kadar geçen süre değişir (ms)
  const handleGone = useCallback(() => {
    setPhase('blurring');
    setTimeout(() => onEnter(), 1000);
  }, [onEnter]);

  // Tıklama/Enter handler — çift tıklamayı engeller
  const handleEnter = useCallback(() => {
    if (phase === 'exiting' || phase === 'blurring') return;
    setPhase('exiting');
  }, [phase]);

  // Fare pozisyonunu -1 ile 1 arasında normalize eder
  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    mousePos.current = {
      x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
      y: -((e.clientY - rect.top) / rect.height) * 2 + 1,
    };
  };
  // Mobil: telefonu eğince logo o yöne döner
  useEffect(() => {
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    if (!isMobile) return;

    let baseAlpha = null;
    let baseBeta = null;

    const handleOrientation = (e) => {
      if (baseBeta === null) {
        baseAlpha = e.alpha ?? 0;
        baseBeta = e.beta ?? 0;
      }
      // gamma: sağa/sola eğim (-90 ile 90 arası) → x ekseni
      // beta:  ileri/geri eğim (-180 ile 180 arası) → y ekseni
      const x = (e.gamma ?? 0) / 45;                    // -1 ile 1 arasına normalize
      const y = -((e.beta ?? 0) - baseBeta) / 45;
      mousePos.current = {
        x: Math.max(-1, Math.min(1, x)),
        y: Math.max(-1, Math.min(1, y)),
      };
    };

    // iOS 13+ izin gerektirir
    if (typeof DeviceOrientationEvent?.requestPermission === 'function') {
      DeviceOrientationEvent.requestPermission()
        .then(res => {
          if (res === 'granted') {
            window.addEventListener('deviceorientation', handleOrientation);
          }
        })
        .catch(() => { });
    } else {
      window.addEventListener('deviceorientation', handleOrientation);
    }

    return () => window.removeEventListener('deviceorientation', handleOrientation);
  }, []);

  const isExiting = phase === 'exiting' || phase === 'blurring';

  const welcomeText = apiSettings?.splash_welcome_text ?? CONFIG.WELCOME_TEXT;
  const ctaText = apiSettings?.splash_cta_text ?? CONFIG.CTA_TEXT;
  const showSocials = apiSettings ? (apiSettings.splash_show_socials !== false) : true;
  const splashBgColor = apiSettings?.splash_bg_color ?? '#030303';
  const logoScale = apiSettings?.splash_logo_scale ?? CONFIG.LOGO_SCALE;
  const cameraZ = apiSettings?.splash_camera_z ?? CONFIG.CAMERA_Z;
  const autoRotate = apiSettings?.splash_auto_rotate ?? CONFIG.AUTO_ROTATE_SPEED;
  const metalness = apiSettings?.splash_metalness ?? CONFIG.METALNESS;
  const roughness = apiSettings?.splash_roughness ?? CONFIG.ROUGHNESS;
  // Galaktik nebula arka planı ayarları
  const bg3dEnabled = apiSettings ? (apiSettings.splash_bg3d_enabled !== false) : true;
  const galaxyColor1 = apiSettings?.splash_galaxy_color1 ?? '#1b2a6e';
  const galaxyColor2 = apiSettings?.splash_galaxy_color2 ?? '#bc13fe';
  const galaxyColor3 = apiSettings?.splash_galaxy_color3 ?? '#00f0ff';
  const galaxyIntensity = apiSettings?.splash_galaxy_intensity ?? 0.9;
  const galaxySpeed = apiSettings?.splash_galaxy_speed ?? 1.0;
  const mouseDisperse = apiSettings ? (apiSettings.splash_mouse_disperse !== false) : true;
  const exitCamera = apiSettings ? (apiSettings.splash_exit_camera !== false) : true;
  // Dalış animasyonu ince ayarları (admin panelden)
  const exitSpeed = apiSettings?.splash_exit_speed ?? CONFIG.EXIT_SPEED;
  const warpSpeed = apiSettings?.splash_warp_speed ?? 1.0;
  const warpBright = apiSettings?.splash_warp_brightness ?? 1.0;
  const warpTwist = apiSettings?.splash_warp_twist ?? 0.5;
  const warpFov = apiSettings?.splash_warp_fov ?? 80;
  const activeSocials = showSocials && apiSettings
    ? SOCIAL_PLATFORMS.filter(p => apiSettings[p.key])
    : [];

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="splash"
        ref={containerRef}
        className="fixed inset-0 z-[9999] overflow-hidden"
        style={{ background: splashBgColor ?? '#030303' }} // ← Arka plan rengi
        onMouseMove={handleMouseMove}
      >

        {/* ── Katman 1: Radial gradient ── */}
        {/* Ortada hafif koyu alan, logonun etrafı daha karanlık */}
        {/* Rengi veya boyutu değiştirmek için ellipse değerlerini ayarla */}
        <motion.div
          className="absolute inset-0 z-0"
          style={{ background: 'radial-gradient(ellipse 55% 55% at 50% 50%, rgba(5, 5, 5, 0.98) 0%, #030303 100%)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 3, delay: 0.5 }} // ← Gradient'in belirme süresi (sn)
        />

        {/* ── Katman 2: Film grain dokusu ── */}
        {/* Sinematik film hissi için ince gürültü — opacity'yi artırınca daha belirgin olur */}
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          style={{
            opacity: 0.019, // ← Grain yoğunluğu (0.01–0.05 arası iyi görünür)
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            backgroundSize: '120px',
          }}
        />

        {/* ── Katman 3: Giriş siyah kapağı ── */}
        {/* Canvas açılırken üstünde duran siyah perde — blur ile birlikte kaybolur */}
        {/* INTRO_BLUR_DURATION değerini değiştirince açılma hızı değişir */}
        <motion.div
          className="absolute inset-0 z-30 pointer-events-none bg-[#030303]"
          initial={{ opacity: 1 }}
          animate={{ opacity: showCanvas ? 0 : 1 }}
          transition={{ duration: CONFIG.INTRO_BLUR_DURATION, ease: [0.16, 1, 0.3, 1] }}
        />

        {/* ── Katman 4: 3D Canvas ── */}
        {/* Tüm Three.js sahnesini içerir — kamera ayarları için CONFIG kullan */}
        {showCanvas && (
          <motion.div
            className="absolute inset-0 z-10"
            initial={{ opacity: 0, filter: 'blur(24px)' }} // ← Başlangıç blur miktarı
            style={{ background: splashBgColor ?? '#030303' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            transition={{ duration: CONFIG.INTRO_BLUR_DURATION, ease: [0.16, 1, 0.3, 1] }}
          >
            <Canvas
              camera={{ position: [0, 0, cameraZ ?? CONFIG.CAMERA_Z], fov: CONFIG.CAMERA_FOV }}
              dpr={[1, 2]} // Retina ekran desteği
              gl={{ antialias: true, powerPreference: 'high-performance', alpha: false }}
              shadows
              onClick={handleEnter} // ← Canvas'a tıklayınca da girilir
              className="cursor-pointer"
            >
              <SceneSetup />
              <ExitCameraRig isExiting={isExiting} baseZ={cameraZ ?? CONFIG.CAMERA_Z} baseFov={CONFIG.CAMERA_FOV}
                enabled={exitCamera} exitSpeed={exitSpeed} targetFov={warpFov} twist={warpTwist} />
              <Suspense fallback={null}>
                {/* Galaktik nebula arka planı */}
                {bg3dEnabled && (
                  <GalaxyBackdrop
                    mousePos={mousePos}
                    isExiting={isExiting}
                    color1={galaxyColor1}
                    color2={galaxyColor2}
                    color3={galaxyColor3}
                    intensity={galaxyIntensity}
                    speed={galaxySpeed}
                    disperse={mouseDisperse}
                    warpExit={exitCamera}
                    warpSpeed={warpSpeed}
                    warpBright={warpBright}
                    twist={warpTwist}
                    exitSpeed={exitSpeed}
                  />
                )}
                <ChromeLights
                  mousePos={mousePos}
                  rimColor={apiSettings?.splash_rim_color ?? CONFIG.LIGHT_RIM_COLOR}
                  accentColor={apiSettings?.splash_accent_color ?? CONFIG.LIGHT_ACCENT_COLOR}
                />
                <KnoxopiaLogoModel
                  modelPath={CONFIG.MODEL_PATH}
                  mousePos={mousePos}
                  isExiting={isExiting}
                  onGone={handleGone}
                  logoScale={logoScale}
                  autoRotate={autoRotate}
                  metalness={metalness}
                  roughness={roughness}
                  exitSpeed={exitSpeed}
                  spinExit={!exitCamera}
                />
                <Particles
                  isExiting={isExiting}
                  count={apiSettings?.splash_particle_count ?? CONFIG.PARTICLE_COUNT}
                  color={apiSettings?.splash_particle_color ?? CONFIG.PARTICLE_COLOR}
                  size={apiSettings?.splash_particle_size ?? CONFIG.PARTICLE_SIZE}
                />
                <Preload all />
              </Suspense>
            </Canvas>
          </motion.div>
        )}

        {/* ── Katman 5: UI Yazıları ── */}
        {/* justify-between + py-14: üst yazı en üstte, alt yazı en altta sabitlenir */}
        {/* Logoya çakışmaz çünkü Canvas absolute pozisyonda, yazılar flex layout'ta */}
        <div className="absolute inset-0 z-20 pointer-events-none flex flex-col items-center justify-between py-14 px-4">

          {/* Üst yazı: "Welcome to" */}
          <AnimatePresence>
            {phase === 'idle' && (
              <motion.span
                key="welcome"
                style={{
                  fontFamily: CONFIG.FONT_FAMILY,
                  fontSize: '10px',           // ← Yazı boyutu
                  letterSpacing: '0.55em',    // ← Harf aralığı
                  textTransform: 'uppercase',
                  color: apiSettings?.splash_text_color ?? 'rgba(255,255,255,0.9)',
                  textShadow: apiSettings?.splash_text_glow
                    ? `0 0 20px ${apiSettings.splash_text_color ?? '#ffffff'}80, 0 0 40px ${apiSettings.splash_text_color ?? '#ffffff'}30`
                    : 'none',
                }}
                initial={{ opacity: 0, y: -10, filter: 'blur(10px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(14px)' }}
                transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
              >
                {welcomeText}
              </motion.span>
            )}
          </AnimatePresence>

          {/* Orta boşluk — logo bu alanda float eder */}
          <div className="flex-1" />

          {/* Alt yazı: "Initialize Knoxopia" + ince çizgi */}
          <AnimatePresence>
            {phase === 'idle' && (
              <motion.div
                key="cta"
                className="flex flex-col items-center gap-3 pointer-events-auto"
                initial={{ opacity: 0, y: 10, filter: 'blur(10px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(14px)' }}
                transition={{ duration: 1.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              >
                {activeSocials.length > 0 && (
                  <motion.div
                    className="flex items-center gap-2 flex-wrap justify-center"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {activeSocials.map(p => (
                      <SocialIcon key={p.key} href={apiSettings[p.key]} color={p.color} d={p.d} label={p.label} isImdb={p.isImdb} />
                    ))}
                  </motion.div>
                )}

                {/* İnce dikey çizgi — yazının üstündeki dekoratif eleman */}
                <motion.div
                  style={{
                    width: '1px',
                    height: '10px', // ← Çizgi yüksekliği
                    background: 'rgba(255, 255, 255, 0.12)',
                    transformOrigin: 'top',
                  }}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ duration: 1.2, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                />

                {/* CTA butonu — tıklayınca da çalışır */}
                <motion.span
                  style={{
                    fontFamily: CONFIG.FONT_FAMILY,
                    fontSize: '10px',           // ← Yazı boyutu
                    letterSpacing: '0.50em',    // ← Harf aralığı
                    textTransform: 'uppercase',
                    color: apiSettings?.splash_text_color ?? 'rgba(255,255,255,0.9)',
                    textShadow: apiSettings?.splash_text_glow
                      ? `0 0 15px ${apiSettings.splash_text_color ?? '#ffffff'}80, 0 0 30px ${apiSettings.splash_text_color ?? '#ffffff'}40`
                      : 'none',
                    cursor: 'pointer',
                  }}
                  // Nefes alır gibi parlayıp sönen animasyon
                  // [min, max, min] değerlerini değiştir
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
                  onClick={handleEnter}
                >
                  {ctaText}
                </motion.span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Katman 6: Çıkış fade ── */}
        {/* Logo tamamen kaybolunca bu siyah overlay açılır */}
        {/* duration değerini değiştirince fade hızı değişir (sn) */}
        <AnimatePresence>
          {phase === 'blurring' && (
            <motion.div
              className="absolute inset-0 z-35 pointer-events-none bg-[#030303]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
            />
          )}
        </AnimatePresence>

      </motion.div>
    </AnimatePresence>
  );
};